import { useState } from 'react'
import { Button, Input, Modal, Select } from '@/components/ui'
import { useRoles } from '@/features/authorization'
import { useT } from '@/internationalization/i18n'
import type { User } from '@/types'
import { useInviteMember } from '../hooks'
import { isValidInviteEmail, visibleInviteRoles } from './inviteMemberForm'

/**
 * Send a new member an invitation.
 *
 * There is no accept-link token to show or copy: the directory admits a new
 * account by matching an unaccepted invitation on email at sign-in (same
 * mechanism `CreateTenantModal` relies on for a tenant owner), so the only
 * thing this dialog collects is the address and the role it should land in.
 */
export function InviteMemberModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const t = useT()
  const roles = useRoles()
  const invite = useInviteMember()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<User['role']>('member')

  const reset = () => {
    setEmail('')
    setRole('member')
  }

  const handleClose = () => {
    if (invite.isPending) return
    reset()
    onClose()
  }

  const complete = isValidInviteEmail(email)
  const roleOptions = visibleInviteRoles(roles.data ?? []).map((r) => ({ value: r.key, label: r.name }))

  const submit = () => {
    if (!complete) return
    invite.mutate(
      { email: email.trim(), role },
      {
        onSuccess: () => {
          reset()
          onClose()
        },
      },
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('members.invite.title')}
      description={t('members.invite.description')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={handleClose} disabled={invite.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" isLoading={invite.isPending} disabled={!complete} onClick={submit}>
            {t('members.invite.submit')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label={t('members.invite.emailLabel')}
          type="email"
          placeholder={t('members.invite.emailPlaceholder')}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={invite.isPending}
          required
          autoFocus
        />
        <Select
          label={t('members.invite.roleLabel')}
          options={roleOptions.length > 0 ? roleOptions : [{ value: 'member', label: t('members.invite.roleLabel') }]}
          value={role}
          onChange={(event) => setRole(event.target.value as User['role'])}
          disabled={invite.isPending}
        />
      </div>
    </Modal>
  )
}
